import { useEffect, useState } from 'react';

import { Button, Dialog, DialogProps } from 'ui-kit';
import toast from 'react-hot-toast';

import { VarianceCode, VarianceStatus } from '__generated__/globalTypes';

import { useUpdateRemittanceVarianceStatus } from './updateRemittanceVarianceStatus_gql';
import type { AccountVariance } from './getAccountRemittanceVariances_gql';

const VARIANCE_LABELS: Record<VarianceCode, string> = {
  [VarianceCode.BALANCE_INCREASED]: 'Balance increased',
  [VarianceCode.BALANCE_EXCEEDS_ORIGINAL]: 'Balance > original',
  [VarianceCode.DPD_REVERSED]: 'DPD reversed',
  [VarianceCode.DPD_OUT_OF_RANGE]: 'DPD out of range',
};

const NOTE_MAX_CHARS = 4000;

interface VarianceNoteDialogProps {
  dialog: DialogProps;
  /** Account + upload context. */
  accountId: string;
  /** All variances detected on this account for the upload's payment month — drives
   *  the left-column badges. One of them is `focusedVariance` (the one the seller
   *  clicked to open the modal). */
  variances: AccountVariance[];
  focusedVariance: AccountVariance | null;
  /** Called after a successful save so the parent can refetch the list. Awaited
   *  before the modal closes so a quick reopen sees fresh `version` / `note` /
   *  `status` instead of the stale snapshot that opened the dialog. */
  onSaved: () => Promise<void> | void;
}

/**
 * Seller-side modal for reviewing variances detected on a remittance record. Shows all
 * variance codes on the account as inline badges (left column) and a notes textarea on
 * the right. Save persists the note; Cancel resets to the server's current note. Ack
 * flips the variance status PENDING → ACKNOWLEDGED; it can be combined with a note or
 * done note-less. Re-opening an already-acknowledged variance shows the same view but
 * disables the Acknowledge button.
 */
const VarianceNoteDialog = ({
  dialog,
  accountId,
  variances,
  focusedVariance,
  onSaved,
}: VarianceNoteDialogProps): JSX.Element | null => {
  const serverNote = focusedVariance?.note ?? '';
  const [noteText, setNoteText] = useState<string>(serverNote);

  // When the modal opens or the focused variance changes, seed the textarea from the
  // server copy. Closing the dialog doesn't reset anything — next open will re-seed.
  useEffect(() => {
    if (dialog.isOpen) setNoteText(serverNote);
  }, [dialog.isOpen, serverNote]);

  const [updateStatus] = useUpdateRemittanceVarianceStatus();
  // Local pending spans the full save cycle (mutation → parent refetch → close).
  // useMutation's `loading` drops to false as soon as the mutation resolves, which
  // would re-enable the action buttons during the refetch window — a double-click
  // there fires a second mutation with the stale `focusedVariance.version` and
  // trips the optimistic-lock.
  const [pending, setPending] = useState(false);

  const dirty = noteText !== serverNote;
  const remaining = NOTE_MAX_CHARS - noteText.length;
  const isAcknowledged = focusedVariance?.status === VarianceStatus.ACKNOWLEDGED;

  if (!focusedVariance) return null;

  const performUpdate = async (nextStatus: VarianceStatus) => {
    if (pending) return;
    setPending(true);
    try {
      await updateStatus({
        variables: {
          input: {
            upload_id: focusedVariance.upload_id,
            account_id: focusedVariance.account_id,
            variance_code: focusedVariance.variance_code,
            status: nextStatus,
            note: noteText,
            expected_version: focusedVariance.version,
          },
        },
      });
      // Await the parent refetch so the next dialog open reads the latest server
      // state (fresh `version` / `note` / `status`) rather than the snapshot that
      // opened this instance — otherwise a quick reopen shows stale data and the
      // subsequent mutation fails the optimistic-lock check.
      await onSaved();
      dialog.setIsOpen(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to save variance: ${msg || 'unknown error'}`);
    } finally {
      setPending(false);
    }
  };

  const handleSave = () => performUpdate(focusedVariance.status);
  const handleAcknowledge = () => performUpdate(VarianceStatus.ACKNOWLEDGED);
  const handleCancel = () => {
    // Reset to server state without closing — consistent with the user's "Cancel resets"
    // expectation. Pair with the ✕ close button for an explicit dismiss.
    setNoteText(serverNote);
  };

  return (
    <Dialog dialog={dialog}>
      <button
        className="absolute right-[10px] top-[10px] text-gray-500 hover:text-gray-300"
        onClick={() => dialog.setIsOpen(false)}
        aria-label="Close"
      >
        ✕
      </button>
      <h2 className="mb-1 text-xl font-semibold">
        Variance — {VARIANCE_LABELS[focusedVariance.variance_code]}
      </h2>
      <div className="text-sm text-gray-400">
        Account {accountId} · {focusedVariance.payment_month}
      </div>
      <div className="mt-4 flex gap-4">
        <div className="flex w-48 flex-shrink-0 flex-col gap-2">
          <div className="text-xs uppercase tracking-wide text-gray-400">Detected codes</div>
          {variances.map((v) => (
            <Badge
              key={v.variance_code}
              code={v.variance_code}
              focused={v.variance_code === focusedVariance.variance_code}
            />
          ))}
        </div>
        <div className="flex flex-1 flex-col">
          <label
            htmlFor="variance-note"
            className="mb-1 text-xs uppercase tracking-wide text-gray-400"
          >
            Note
          </label>
          <textarea
            id="variance-note"
            className="h-32 w-full rounded-md border border-pink-400 bg-background-surface p-2 text-sm text-foreground-default focus:border-pink-400 focus:outline-none"
            value={noteText}
            maxLength={NOTE_MAX_CHARS}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Optional note explaining the cause of this variance"
          />
          <div className="mt-1 flex justify-between text-xs text-gray-400">
            <span>{isAcknowledged ? 'Acknowledged' : 'Pending'}</span>
            <span>{remaining} chars remaining</span>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-between gap-2">
        <Button
          type="secondary"
          label="cancel-variance-note"
          size="medium"
          disabled={!dirty || pending}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <div className="flex gap-2">
          <Button
            type="secondary"
            label="save-variance-note"
            size="medium"
            disabled={!dirty || pending}
            onClick={handleSave}
          >
            Save Note
          </Button>
          <Button
            label="acknowledge-variance"
            size="medium"
            disabled={isAcknowledged || pending}
            onClick={handleAcknowledge}
          >
            {isAcknowledged ? 'Acknowledged' : 'Acknowledge'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

/**
 * Pill badge for one variance code, with optional focus highlight for the code the
 * modal was opened against.
 */
const Badge = ({ code, focused }: { code: VarianceCode; focused: boolean }): JSX.Element => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      focused
        ? 'bg-pink-500/20 text-pink-300 ring-1 ring-pink-400'
        : 'bg-slate-500/20 text-slate-300'
    }`}
  >
    {VARIANCE_LABELS[code]}
  </span>
);

export default VarianceNoteDialog;
